import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { ExtendedTable, Header, TableSettingsToolbar } from '@app/components'
import { ContentLayout } from '@app/layouts'

import { EXTERNAL_PRODUCTS_CATALOG_KEY } from '@app/constants/queryKeys'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'

import { useNotification, usePaginationQueryParams, useTableSearch, useTableSetting } from '@app/hooks'
import { patchTypes } from '@app/api/routes/monitorings'

import { getManagingTypes } from '@app/api/routes/pricing'

import { getColumns, adaptManagingMonitoringsTableData } from './lib'
import { useIsActiveColumn } from './hooks/useIsActiveColumn'
import { tableSearchQuerySchema, columnsSearchSchema } from './configs'

import { usePricingLevelEditableColumn } from './hooks'

import type { PatchTypesData } from '@app/api/routes/monitorings/types'

const columns = getColumns()

const title = 'Управление активными мониторингами'

export const ManagingMonitoringsPage: React.FC = () => {
  const { paginationParams, pagination, setPagination } = usePaginationQueryParams()

  const notification = useNotification()
  const queryClient = useQueryClient()

  const patchItemMutation = useMutation({
    mutationFn: patchTypes,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList, paginationParams]
      })

      // TODO согласовать текст с Андреем З
      notification.success({
        message: 'Успешно изменено',
        description: 'Успешное изменение мониторинга'
      })
    }
  })

  const { data: managingMonitoringsTableData, isLoading: isDataListsItemsLoading } = useQuery({
    queryKey: [EXTERNAL_PRODUCTS_CATALOG_KEY.externalProductsList, paginationParams],
    queryFn: async () => await getManagingTypes(paginationParams),
    select: managingTypesData => adaptManagingMonitoringsTableData(managingTypesData)
  })

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handlePatchItem = (data: PatchTypesData): void => {
    patchItemMutation.mutate(data)
  }

  useIsActiveColumn(columns, handlePatchItem)

  const columnsWithPricingLevels = usePricingLevelEditableColumn({
    columns
  })

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.managingMonitoringsPage, columnsWithPricingLevels)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredListsItemsDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: managingMonitoringsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const toolbar = (
    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  )

  return (
    <>
      <Header controlProps={ { title } } />

      <ContentLayout>
        <ExtendedTable
          loading={ isDataListsItemsLoading }
          columns={ columnsWithSearchTool }
          dataSource={ filteredListsItemsDataSource }
          toolbar={ toolbar }
          pagination={ {
            ...pagination,
            total: managingMonitoringsTableData?.count,
            onChange: handlePaginationChange
          } }
        />
      </ContentLayout>
    </>
  )
}
